import React, { useState } from "react";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";

import { Icon } from "../../../components/Component";
import { LinkList, LinkItem } from "../../../components/links/Links";
import { findUpper } from "../../../utils/Utils";
import { performLogout } from "../../../auth/logout";
import { logout } from "../../../store/slices/currentUserSlice";
import type { RootState } from "../../../store/store";

/**
 * Slim version of buyer's User dropdown — initials avatar, name + role,
 * and the menu (View Profile / Security Setting / Sign Out). No FCM/SW
 * cleanup since the shell doesn't register them.
 */
const UserDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state: RootState) => state.currentUser);
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);

  const onLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logout());
    // Server-side revocation, token/storage clear, cross-tab broadcast and
    // the /login redirect.
    await performLogout("user");
  };

  const firstName = data?.firstName ?? "";
  const lastName = data?.lastName ?? "";
  const initials = findUpper(`${firstName} ${lastName}`.trim() || "User");
  const fullName = `${firstName} ${lastName}`.trim() || "Signed-in user";
  const role = data?.roleName || data?.genericRole || "";
  const email = data?.email || "";

  return (
    <Dropdown isOpen={open} className="user-dropdown" toggle={toggle}>
      <DropdownToggle
        tag="a"
        href="#toggle"
        className="dropdown-toggle"
        onClick={(ev) => ev.preventDefault()}
      >
        <div className="user-card sm">
          <div className="user-avatar">
            <span>{initials}</span>
          </div>
          <div className="user-info">
            <span className="lead-text">{fullName}</span>
            {role && (
              <span className="sub-text" title={role}>
                {role}
              </span>
            )}
          </div>
        </div>
      </DropdownToggle>
      <DropdownMenu end className="dropdown-menu-md dropdown-menu-s1">
        <div className="dropdown-inner user-card-wrap bg-lighter d-none d-md-block">
          <div className="user-card sm">
            <div className="user-avatar">
              <span>{initials}</span>
            </div>
            <div className="user-info">
              <span className="lead-text">{fullName}</span>
              {email && (
                <span className="sub-text" title={email}>
                  {email.length > 20 ? email.substring(0, 20) + "..." : email}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="dropdown-inner">
          <LinkList>
            <LinkItem link={"/profile"} icon="user-alt" onClick={toggle}>
              View Profile
            </LinkItem>
            <LinkItem link={"/profile"} icon="setting-alt" onClick={toggle}>
              Security Setting
            </LinkItem>
          </LinkList>
        </div>
        <div className="dropdown-inner">
          <LinkList>
            <a href="#signout" onClick={onLogout}>
              <Icon name="signout" />
              <span>Sign Out</span>
            </a>
          </LinkList>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default UserDropdown;
